import {Icon} from '@wandb/components';
import {
  Content as DropdownContent,
  Portal as DropdownPortal,
  Root as DropdownRoot,
  Separator as DropdownSeparator,
} from '@wandb/weave/components/DropdownMenu';
import React, {useCallback, useState} from 'react';
import {useHistory} from 'react-router-dom';

import {useRunDetailsTo} from '../../../components/RunDetails/hooks';
import {SlurmJobConfirmModal} from '../../../components/Slurm/SlurmJobConfirmModal';
import {useSlurmJobActions} from '../../../components/Slurm/useSlurmJobActions';
import {useIsInLoadedReportContext} from '../../../pages/ReportPage/LoadedReportContext';
import {RenameRunDialog} from '../../../pages/Run/RenameRunDialog';
import {
  type RunMenuAction,
  trackRunMenu,
} from '../../../services/analytics/runMenuEvents';
import {type Ref as CustomRunNamesRef} from '../../../state/views/customRunNames/types';
import {useStatsigGateWorkspaceAgent} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {type WBTableRowWithRunSetInfo} from '../../../util/tablecols';
import {useWBAgentContextStore} from '../../WBAgent/WBAgentContextStore';
import {useRunSelectorFeatureEnabled} from '../RunSelectorFeaturesContext';
import {
  useRunSelectorProjectInfo,
  useRunSetGroupSelectionGrouping,
} from '../RunSelectorRunSetContext';
import {isCrossProjectRun as isCrossProjectRunFunc} from '../util';
import {RowHeaderMenuOption} from './RowHeaderMenuOption';
import {RowHeaderMenuTrigger} from './RowHeaderMenuTrigger';
import {RunCopyIdentifiersSubmenu} from './RunCopyIdentifiersSubmenu';
import {RunPinningAndBaselineMenuOptions} from './RunPinningAndBaselineMenuOptions';

function useCanPinRuns() {
  const isEnabled = useRunSelectorFeatureEnabled();
  const isPinningRunsEnabled = isEnabled('pinning');
  const grouping = useRunSetGroupSelectionGrouping();
  return isPinningRunsEnabled && grouping.length === 0;
}

type RunRowHeaderMenuProps = {
  row: WBTableRowWithRunSetInfo;
  customRunNamesRef: CustomRunNamesRef | undefined;
  onCopyRunName: () => void;
  onCopyRunPath: () => void;
  onDeleteRun: () => void;
  onMoveRun: () => void;
  onStopRun: () => void;
  canStopRun?: boolean;
  visible: boolean;
};

export function RunRowHeaderMenu(props: RunRowHeaderMenuProps) {
  const [menuOpen, setMenuOpenRaw] = useState(false);
  const [hasPrefetched, setHasPrefetched] = useState(false);
  const setMenuOpen = useCallback((open: boolean) => {
    setMenuOpenRaw(open);
    if (open) {
      setHasPrefetched(true);
    }
  }, []);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const isReport = useIsInLoadedReportContext();
  const isEnabled = useRunSelectorFeatureEnabled();
  const canPinRuns = useCanPinRuns();
  const history = useHistory();
  const trackRunMenuWithLocation = (action: RunMenuAction) => {
    trackRunMenu({action, location: isReport ? 'report' : 'workspace'});
  };
  const runUrl = useRunDetailsTo(props.row.name, props.row.project);

  const {entityName, projectName} = useRunSelectorProjectInfo();
  const runsetProject = {entityName, name: projectName};
  const isCrossProjectRun = isCrossProjectRunFunc(
    props.row.project,
    runsetProject
  );

  const canEditRunName = isEnabled('edit-run-name');
  const canDeleteRun = isEnabled('delete') && !isCrossProjectRun;
  const canMoveRun = isEnabled('move') && !isCrossProjectRun;
  const canStopRun =
    props.canStopRun && isEnabled('stop-run') && !isCrossProjectRun;

  const slurm = useSlurmJobActions({
    entityName,
    projectName,
    runName: props.row.name,
    skip: !hasPrefetched,
  });
  const [confirmVariant, setConfirmVariant] = useState<
    'cancel' | 'requeue' | null
  >(null);
  const isWBAgentEnabled = useStatsigGateWorkspaceAgent();
  const addRunToWBAgentContext = useWBAgentContextStore(s => s.addRun);
  const openWBAgent = useWBAgentContextStore(s => s.setOpen);

  return (
    <>
      <DropdownRoot open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
        <RowHeaderMenuTrigger
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
          visible={menuOpen || props.visible}
          onPrefetch={() => setHasPrefetched(true)}
        />
        <DropdownPortal>
          <DropdownContent
            align="start"
            sideOffset={8}
            alignOffset={-8}
            style={{zIndex: 1}}>
            {isWBAgentEnabled && (
              <RowHeaderMenuOption
                icon={<Icon icon="threads" />}
                label="Add to chat"
                onSelect={() => {
                  openWBAgent(true);
                  addRunToWBAgentContext({
                    name: props.row.name,
                    displayName: props.row.displayName,
                  });
                }}
              />
            )}
            <RowHeaderMenuOption
              icon={<Icon icon="code-alt" />}
              label="View run logs"
              onSelect={() => {
                history.push(runUrl('logs'));
                trackRunMenuWithLocation('View run details');
              }}
            />

            {canPinRuns ? (
              <RunPinningAndBaselineMenuOptions row={props.row} />
            ) : null}
            <DropdownSeparator />
            {canEditRunName && (
              <RowHeaderMenuOption
                icon={<Icon icon="pencil-edit" />}
                label="Edit run name"
                onSelect={() => {
                  setIsRenameDialogOpen(true);
                  trackRunMenuWithLocation('Edit run name');
                }}
              />
            )}
            <RunCopyIdentifiersSubmenu
              onCopyRunName={() => {
                props.onCopyRunName();
                trackRunMenuWithLocation('Copy run name');
              }}
              onCopyRunPath={() => {
                props.onCopyRunPath();
                trackRunMenuWithLocation('Copy run path');
              }}
            />
            {(canMoveRun || canDeleteRun || canStopRun) && (
              <DropdownSeparator />
            )}

            {canMoveRun && (
              <RowHeaderMenuOption
                icon={<Icon icon="forward-next" />}
                label="Move to another group"
                onSelect={() => {
                  props.onMoveRun();
                  trackRunMenuWithLocation('Move run groups');
                }}
              />
            )}
            {canDeleteRun && (
              <RowHeaderMenuOption
                icon={<Icon icon="delete" />}
                label="Delete run"
                onSelect={() => {
                  props.onDeleteRun();
                  trackRunMenuWithLocation('Delete run');
                }}
              />
            )}
            {canStopRun ? (
              <RowHeaderMenuOption
                icon={<Icon icon="stop" />}
                label="Stop run"
                onSelect={() => {
                  props.onStopRun();
                  trackRunMenuWithLocation('Stop run');
                }}
              />
            ) : null}
            {slurm.showSlurmActions &&
              !slurm.readOnly &&
              !isCrossProjectRun &&
              props.row.state === 'running' && (
                <>
                  <DropdownSeparator />
                  <RowHeaderMenuOption
                    icon={<Icon icon="do-not-prohibited" />}
                    label="Cancel Slurm job"
                    onSelect={() => setConfirmVariant('cancel')}
                  />
                  <RowHeaderMenuOption
                    icon={<Icon icon="reset" />}
                    label="Requeue Slurm job"
                    onSelect={() => setConfirmVariant('requeue')}
                  />
                </>
              )}
          </DropdownContent>
        </DropdownPortal>
      </DropdownRoot>
      <RenameRunDialog
        isOpen={isRenameDialogOpen}
        runId={props.row.id}
        runName={props.row.name}
        runDisplayName={props.row.displayName}
        customRunNamesRef={props.customRunNamesRef}
        closeDialog={() => setIsRenameDialogOpen(false)}
        isCrossProjectRun={isCrossProjectRun}
      />
      {slurm.showSlurmActions && (
        <SlurmJobConfirmModal
          isOpen={confirmVariant != null}
          variant={confirmVariant ?? 'cancel'}
          jobId={slurm.jobId}
          entityName={entityName}
          projectName={projectName}
          runName={props.row.name}
          onClose={() => setConfirmVariant(null)}
        />
      )}
    </>
  );
}
